import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../css/ParticipantList.css";
import "../css/EmailHistory.css";
import "../css/TemplateList.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faPlus, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

const EmailHistory = () => {
  const [history, setHistory] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const API_URL = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();

  // Fetch communication history
  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/ems/communication-history`);
      setHistory(Array.isArray(res.data) ? res.data : []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to fetch communication history", err);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        await axios.delete(`${API_URL}/ems/communication-history/${id}`);
        fetchHistory();
      } catch (err) {
        console.error("Failed to delete history", err);
        alert("Delete failed");
      }
    }
  };

  const handleAddcommunication = () => navigate("/ems/templates");
  const handleSendEmail = () => navigate("/ems/send-emails");
  const handleCommunicationHistory = () => navigate("/ems/communication-history");

  // Filter history by search term
  const filteredHistory = history.filter((item) =>
    (item.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.subject || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.body || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination (same as other tables)
  const effectivePerPage = itemsPerPage >= filteredHistory.length ? Math.max(filteredHistory.length, 1) : itemsPerPage;
  const totalPages = Math.max(Math.ceil(filteredHistory.length / effectivePerPage), 1);
  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentHistory = filteredHistory.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;
    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  const truncateText = (text, length = 50) => {
    if (!text) return "-";
    return text.length > length ? text.slice(0, length) + "..." : text;
  };

  return (
    <div className="p-4">
      {/* Header with Search on left and Buttons on right - same as Templates list */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center w-25">
          <div className="me-2" style={{ flex: "1" }}>
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search emails..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search emails"
              style={{ border: "1px solid #d1cfcf" }}
            />
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-success"
            onClick={handleAddcommunication}
            style={{ color: "white", borderRadius: "5px", padding: "8px 12px" }}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Template
          </button>
          <button
            className="btn btn-success"
            onClick={handleSendEmail}
            style={{ color: "white", borderRadius: "5px", padding: "8px 12px" }}
          >
            <FontAwesomeIcon icon={faEnvelope} className="me-2" />
            Send Email
          </button>
          <button
            className="btn btn-success"
            onClick={handleCommunicationHistory}
            style={{ color: "white", borderRadius: "5px", padding: "8px 12px" }}
          >
            Communication History
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="communication-table-container">
        {/* Header */}
        <div className="communication-header">
          <div className="communication-col col-name">Email</div>
          <div className="communication-col col-subject">Subject</div>
          <div className="communication-col col-body">Contents</div>
          <div className="communication-col col-date">Created At</div>
            <div className="communication-col col-reference">Reference</div>
          <div className="communication-col col-actions">Actions</div>
        </div>

        {/* Rows */}
        {currentHistory.length === 0 ? (
          <p className="text-muted p-2">{searchTerm ? "No emails match your search." : "No communication history available"}</p>
        ) : (
          currentHistory.map((t, i) => (
            <React.Fragment key={t._id || i}>
              <div className="communication-row">
                <div className="communication-col col-name">{t.email || "-"}</div>
                <div className="communication-col col-subject">{t.subject || "-"}</div>
                <div className="communication-col col-body">
                  {expandedId === t._id ? t.body : truncateText(t.body, 50)}
                  {t.body && t.body.length > 50 && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 ms-1"
                      onClick={() => toggleExpand(t._id)}
                    >
                      {expandedId === t._id ? "Show Less" : "View Details"}
                    </button>
                  )}
                </div>
                <div className="communication-col col-date">
                  {t.sent_at
                    ? new Date(t.sent_at).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "-"}
                </div>
                <div className="communication-col col-reference">
                  {t.referenceId || "-"}
                </div>

                <div className="communication-col col-actions template-actions">
                  <button
                    className="btn btn-sm action-btn"
                    onClick={() => handleDelete(t._id)}
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              </div>

              {expandedId === t._id && (
                <div className="communication-details p-3 border rounded bg-light mb-2">
                  <p><strong>From Name:</strong> {t.from_name || "-"}</p>
                  <p><strong>From Email:</strong> {t.from_email || "-"}</p>
                  <p><strong>Reference:</strong> {t.referenceId || "-"}</p>
                  <p><strong>Attachments:</strong></p>
                  {t.attachments?.length > 0
                    ? t.attachments.map((f, i) => (
                        <div key={i}>
                          <a href={f.path} target="_blank" rel="noopener noreferrer">
                            {f.filename || `File ${i + 1}`}
                          </a>
                        </div>
                      ))
                    : "-"}
                  <p>
                    <strong>Created At:</strong>{" "}
                    {t.createdAt
                      ? new Date(t.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "-"}
                  </p>
                </div>
              )}
            </React.Fragment>
          ))
        )}

        {/* Pagination inside table - same as other tables */}
        {filteredHistory.length > 0 && (
          <div className="participant-pagination">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: "13px", color: "#666" }}>Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto", fontSize: "13px" }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="pagination-box">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {getVisiblePages().map((num) => (
                <button
                  key={num}
                  className={`pagination-btn ${num === currentPage ? "active" : ""}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailHistory;
